import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { Badge } from '../../components/ui';
import type { ProfitSummary } from './orderProfit';
import './profitReport.css';

/**
 * The Profit section at the top of CG Orders → Reports (drop 241).
 *
 * Kyu, 22 September: "a report or something where we can click on a custom
 * range, like if we want to see how much we earn and what's our profit from
 * September or Q2 ... average margin so far per dollar amount". It follows the
 * report's own time period, date basis and filters, so it always describes the
 * same orders as the table under it.
 */

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const whole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function pct(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}%`;
}

export function ProfitReport({ summary, period }: { summary: ProfitSummary; period: string }) {
  const [open, setOpen] = useState<Set<string>>(() => new Set());

  function toggle(id: string) {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const biggest = summary.rollup[0]?.total ?? 0;

  return (
    <section className="profit-report" aria-label="Profit">
      <h3 className="project-category-heading">
        Profit <Badge tone="brand">{period}</Badge>
      </h3>
      <p className="cg-scorecard-lede">
        Only orders with a supplier bill count. Margin is total profit over total revenue, so it
        reads as profit per $1 of revenue.
      </p>

      <div className="profit-kpis">
        <div className="profit-kpi">
          <span className="profit-kpi-label">Revenue</span>
          <span className="profit-kpi-value">{whole.format(summary.revenue)}</span>
        </div>
        <div className="profit-kpi">
          <span className="profit-kpi-label">Supplier bills</span>
          <span className="profit-kpi-value">{whole.format(summary.bills)}</span>
        </div>
        <div className="profit-kpi">
          <span className="profit-kpi-label">Profit</span>
          <span
            className={`profit-kpi-value ${summary.profit < 0 ? 'profit-loss' : 'profit-gain'}`}
          >
            {whole.format(summary.profit)}
          </span>
        </div>
        <div className="profit-kpi">
          <span className="profit-kpi-label">
            {summary.marginPct === null
              ? 'Margin'
              : `Margin (${String(Math.round(summary.marginPct))}¢ per $1)`}
          </span>
          <span className="profit-kpi-value">{pct(summary.marginPct)}</span>
        </div>
        <div className="profit-kpi">
          <span className="profit-kpi-label">
            Avg profit per order ({summary.billedCount} billed)
          </span>
          <span className="profit-kpi-value">
            {summary.avgProfit === null ? '—' : whole.format(summary.avgProfit)}
          </span>
        </div>
        <div className="profit-kpi">
          <span className="profit-kpi-label">Waiting on a bill</span>
          <span className={`profit-kpi-value ${summary.pendingCount > 0 ? 'profit-pending' : ''}`}>
            {summary.pendingCount === 1 ? '1 order' : `${String(summary.pendingCount)} orders`}
          </span>
        </div>
      </div>

      {summary.rows.length === 0 ? (
        <p className="cg-scorecard-lede">No orders in this period.</p>
      ) : (
        <table className="tracker-table profit-table">
          <thead>
            <tr>
              <th aria-label="Show cost lines" />
              <th>Order</th>
              <th>Client</th>
              <th className="profit-num">Revenue</th>
              <th className="profit-num">Bill</th>
              <th className="profit-num">Profit</th>
              <th className="profit-num">Margin</th>
            </tr>
          </thead>
          <tbody>
            {summary.rows.map(({ order, result, lines }) => {
              const expandable = lines.length > 0;
              const isOpen = open.has(order.id);
              return (
                <Fragment key={order.id}>
                  <tr>
                    <td>
                      {expandable ? (
                        <button
                          type="button"
                          className="profit-toggle"
                          aria-expanded={isOpen}
                          aria-label={`${isOpen ? 'Hide' : 'Show'} cost lines for ${order.order_no}`}
                          onClick={() => toggle(order.id)}
                        >
                          {isOpen ? '▾' : '▸'}
                        </button>
                      ) : null}
                    </td>
                    <td>
                      <Link to={`/cg-orders/${order.id}`}>{order.order_no}</Link>
                    </td>
                    <td>{order.client}</td>
                    <td className="profit-num">{money.format(result.revenue)}</td>
                    <td className="profit-num">
                      {result.bill === null ? '—' : money.format(result.bill)}
                    </td>
                    <td className="profit-num">
                      {result.profit === null ? (
                        <Badge tone="warning">Pending bill</Badge>
                      ) : (
                        money.format(result.profit)
                      )}
                    </td>
                    <td className="profit-num">{pct(result.marginPct)}</td>
                  </tr>
                  {isOpen
                    ? lines.map((line) => (
                        <tr key={line.id} className="profit-line">
                          <td />
                          <td colSpan={3}>{line.label}</td>
                          <td className="profit-num">{money.format(Number(line.amount_usd))}</td>
                          <td colSpan={2} />
                        </tr>
                      ))
                    : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {summary.rollup.length === 0 ? null : (
        <>
          <h4 className="profit-subhead">Where the money goes</h4>
          <table className="profit-rollup">
            <tbody>
              {summary.rollup.map((entry) => (
                <tr key={entry.name}>
                  <td className="profit-rollup-name">{entry.name}</td>
                  <td className="profit-rollup-bar-cell">
                    <span
                      className="profit-rollup-bar"
                      style={{
                        width: `${String(biggest > 0 ? Math.max(2, (entry.total / biggest) * 100) : 0)}%`,
                      }}
                    />
                  </td>
                  <td className="profit-num">{money.format(entry.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
